import { useFeatureFlagEnabled } from "@posthog/react";

import { MARKETPLACE_GO_LIVE_ENABLED_FLAG_KEY } from "../lib/feature-flags";
import { type MarketplaceState } from "../lib/verified-copy";

export type Override = "force-on" | "force-off" | null;

const STORAGE_KEY = `sill.flag-override.${MARKETPLACE_GO_LIVE_ENABLED_FLAG_KEY}`;

// Module-scoped cache: captures the override once per SPA session on first
// read. Survives component remounts (e.g. OnboardingGuard → <Navigate> →
// DashboardPage) that would otherwise re-read a query-param-stripped URL.
// sessionStorage persists the override across full-page reloads within the
// same tab so QA can refresh without losing the override.
let cachedOverride: Override | undefined;

function readOverrideOnce(): Override {
  if (cachedOverride !== undefined) return cachedOverride;
  if (typeof window === "undefined") {
    cachedOverride = null;
    return cachedOverride;
  }

  const fromUrl = parseFlagsParam(window.location.search);
  if (fromUrl !== null) {
    cachedOverride = fromUrl;
    try {
      window.sessionStorage.setItem(STORAGE_KEY, fromUrl);
    } catch {
      // sessionStorage unavailable (privacy mode, SSR shim) — fine, keep
      // the in-memory cache.
    }
    return cachedOverride;
  }

  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored === "force-on" || stored === "force-off") {
      cachedOverride = stored;
      return cachedOverride;
    }
  } catch {
    // sessionStorage unavailable — fall through.
  }

  cachedOverride = null;
  return cachedOverride;
}

function parseFlagsParam(search: string): Override {
  const raw = new URLSearchParams(search).get("flags");
  if (!raw) return null;
  for (const token of raw.split(",").map((t) => t.trim())) {
    if (token === MARKETPLACE_GO_LIVE_ENABLED_FLAG_KEY) return "force-on";
    if (token === `!${MARKETPLACE_GO_LIVE_ENABLED_FLAG_KEY}`) return "force-off";
  }
  return null;
}

/**
 * Test-only: reset the memoized override so tests can simulate separate
 * SPA sessions. Not exported for production consumers.
 */
export function __resetMarketplaceOverrideCacheForTests(): void {
  cachedOverride = undefined;
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Plan 435 M1 (mfh-gate-tristate). Exposes the same memoized `?flags=`/
 * sessionStorage override `useMarketplaceState` reads, for
 * `useConsumerMarketplaceGate` to compose into a tri-state without
 * duplicating the override-parsing logic. Does not change
 * `useMarketplaceState`'s own boolean behavior.
 */
export function readMarketplaceOverride(): Override {
  return readOverrideOnce();
}

export function useMarketplaceState(): MarketplaceState {
  const flagEnabled = useFeatureFlagEnabled(MARKETPLACE_GO_LIVE_ENABLED_FLAG_KEY);
  const override = readOverrideOnce();

  if (override === "force-on") return "post";
  if (override === "force-off") return "pre";
  return flagEnabled === true ? "post" : "pre";
}
